import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ErrorMetrics {
  totalErrors: number;
  criticalErrors: number;
  errorRate: number;
  topErrors: Array<{
    type: string;
    count: number;
    lastOccurred: string;
  }>;
  errorTrend: 'up' | 'down' | 'stable';
}

export default function ErrorTracking() {
  const [metrics, setMetrics] = useState<ErrorMetrics>({
    totalErrors: 0,
    criticalErrors: 0,
    errorRate: 0,
    topErrors: [],
    errorTrend: 'stable'
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadErrorMetrics = async () => {
    try {
      setLoading(true);
      
      // Get system alerts (errors) from last 24 hours
      const { data: alerts, error } = await supabase
        .from('system_alerts')
        .select('*')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate metrics
      const totalErrors = alerts?.length || 0;
      const criticalErrors = alerts?.filter(a => a.severity === 'critical').length || 0;
      
      // Group errors by type
      const errorGroups: Record<string, number> = {};
      const errorLatest: Record<string, string> = {};
      
      alerts?.forEach(alert => {
        const type = alert.alert_type || 'unknown';
        errorGroups[type] = (errorGroups[type] || 0) + 1;
        if (!errorLatest[type] || alert.created_at > errorLatest[type]) {
          errorLatest[type] = alert.created_at;
        }
      });

      const topErrors = Object.entries(errorGroups)
        .map(([type, count]) => ({
          type,
          count,
          lastOccurred: errorLatest[type]
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Calculate error rate (errors per hour)
      const errorRate = totalErrors / 24;

      // Mock trend calculation (in production, compare with previous period)
      const errorTrend: 'up' | 'down' | 'stable' = 
        errorRate > 5 ? 'up' : errorRate < 1 ? 'down' : 'stable';

      setMetrics({
        totalErrors,
        criticalErrors,
        errorRate,
        topErrors,
        errorTrend
      });

    } catch (error) {
      console.error('Error loading error metrics:', error);
      toast({
        title: "Error Loading Metrics",
        description: "Failed to load error tracking data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadErrorMetrics();
    
    // Refresh every 5 minutes
    const interval = setInterval(loadErrorMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getTrendIcon = () => {
    switch (metrics.errorTrend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-destructive" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-green-500" />;
      default: return <div className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="pt-6">
                <div className="h-8 bg-muted rounded mb-2"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Error Tracking Dashboard</h2>
        <Button onClick={loadErrorMetrics} size="sm" variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Error Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Errors (24h)</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalErrors}</div>
            <div className="flex items-center text-xs text-muted-foreground">
              {getTrendIcon()}
              <span className="ml-1">
                {metrics.errorRate.toFixed(1)} errors/hour
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical Errors</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{metrics.criticalErrors}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.totalErrors > 0 
                ? `${((metrics.criticalErrors / metrics.totalErrors) * 100).toFixed(1)}% of total`
                : 'No errors'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Status</CardTitle>
            <div className={`h-3 w-3 rounded-full ${
              metrics.criticalErrors === 0 ? 'bg-green-500' : 'bg-destructive'
            }`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics.criticalErrors === 0 ? 'Healthy' : 'Issues'}
            </div>
            <p className="text-xs text-muted-foreground">
              {metrics.criticalErrors === 0 
                ? 'No critical errors detected'
                : `${metrics.criticalErrors} critical issues`
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Error Types */}
      <Card>
        <CardHeader>
          <CardTitle>Top Error Types (Last 24 Hours)</CardTitle>
        </CardHeader>
        <CardContent>
          {metrics.topErrors.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No errors detected in the last 24 hours
            </p>
          ) : (
            <div className="space-y-3">
              {metrics.topErrors.map((error, index) => (
                <div key={error.type} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{error.type}</span>
                      <Badge variant="outline">{error.count} occurrences</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Last occurred: {new Date(error.lastOccurred).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">#{index + 1}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Alerts */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span>Database Response Time</span>
              <Badge variant="secondary">Normal</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>Edge Function Performance</span>
              <Badge variant="secondary">Good</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>Memory Usage</span>
              <Badge variant="secondary">Optimal</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span>API Rate Limits</span>
              <Badge variant="secondary">Within Limits</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}